import React from "react";
import { Link } from "react-router-dom";
import {
  Sparkles,
  Building2,
  Truck,
  HardHat,
  AppWindow,
  Home as HomeIcon,
  ArrowRight,
  Phone,
  Check,
  Star,
  Quote,
} from "lucide-react";
import { Button } from "../components/ui/button";
import Reveal from "../components/Reveal";
import Counter from "../components/Counter";
import { COMPANY, HERO, STATS, SERVICE_PREVIEWS, STEPS, TESTIMONIALS } from "../mock";

const iconMap = { Sparkles, Building2, Truck, HardHat, AppWindow, Home: HomeIcon };

const Home = () => {
  return (
    <div className="overflow-hidden">
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-sky-50 via-sky-50/40 to-white">
        {/* decorative animated blobs */}
        <div className="pointer-events-none absolute -left-24 top-10 h-72 w-72 rounded-full bg-sky-200/40 blur-3xl animate-blob" />
        <div className="pointer-events-none absolute -right-16 bottom-0 h-80 w-80 rounded-full bg-cyan-200/40 blur-3xl animate-blob" style={{ animationDelay: "3s" }} />

        <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-5 py-14 lg:grid-cols-2 lg:gap-16 lg:px-8 lg:py-24">
          <div className="animate-fade-up">
            <span className="mb-5 inline-flex items-center gap-2 rounded-full bg-sky-100 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-sky-700 animate-soft-pulse">
              <Star size={14} className="fill-sky-500 text-sky-500" /> {COMPANY.slogan}
            </span>
            <h1 className="text-4xl font-extrabold leading-[1.1] tracking-tight text-slate-900 sm:text-5xl lg:text-[3.25rem]">
              {HERO.titlePlain}
              <span className="relative text-sky-500">
                {HERO.titleAccent}
                <svg className="absolute -bottom-2 left-0 w-full" height="10" viewBox="0 0 200 10" preserveAspectRatio="none">
                  <path d="M2 7 Q 100 -2 198 6" stroke="#0ea5e9" strokeWidth="3" fill="none" strokeLinecap="round" />
                </svg>
              </span>
              {HERO.titleRest}
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-slate-600">
              {HERO.subtitle}
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link to="/contact">
                <Button className="btn-shine h-12 w-full rounded-full bg-sky-500 px-8 text-base font-semibold text-white shadow-lg shadow-sky-500/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-sky-600 hover:shadow-xl sm:w-auto">
                  Demander un devis gratuit
                </Button>
              </Link>
              <a href={`tel:${COMPANY.phoneRaw}`}>
                <Button
                  variant="outline"
                  className="h-12 w-full rounded-full border-slate-300 px-8 text-base font-semibold text-slate-700 transition-all duration-300 hover:-translate-y-0.5 hover:border-sky-400 hover:text-sky-600 sm:w-auto"
                >
                  <Phone size={18} className="mr-2" /> {COMPANY.phone}
                </Button>
              </a>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3">
              {HERO.bullets.map((b, i) => (
                <div
                  key={b}
                  className="flex items-center gap-2 text-sm font-medium text-slate-700 animate-fade-up"
                  style={{ animationDelay: `${0.2 + i * 0.15}s`, opacity: 0, animationFillMode: "forwards" }}
                >
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-sky-500 text-white">
                    <Check size={12} strokeWidth={3} />
                  </span>
                  {b}
                </div>
              ))}
            </div>
          </div>
          <div className="animate-fade-up">
            <div className="relative animate-floaty">
              <div className="absolute -right-4 -top-4 h-full w-full rounded-3xl bg-gradient-to-br from-sky-300/60 to-cyan-200/40" />
              <img
                src={HERO.image}
                alt="Équipe de nettoyage WBL Services en intervention"
                className="relative h-[380px] w-full rounded-3xl object-cover shadow-2xl lg:h-[520px]"
              />
              {/* floating rating card */}
              <div className="absolute -bottom-6 left-6 flex items-center gap-3 rounded-2xl bg-white/95 p-4 shadow-xl backdrop-blur">
                <div className="flex flex-col">
                  <div className="flex gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={16} className="fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <span className="mt-1 text-sm font-bold text-slate-900">Service 5 étoiles</span>
                  <span className="text-xs text-slate-500">Toulouse & environs</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICE 5 ETOILES */}
      <section className="relative overflow-hidden bg-slate-900 py-16 lg:py-20">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(14,165,233,0.15),transparent_40%)]" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal className="mb-12 text-center">
            <div className="mb-3 flex justify-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={22} className="fill-amber-400 text-amber-400" />
              ))}
            </div>
            <h2 className="text-2xl font-bold text-white sm:text-3xl">Service 5 étoiles</h2>
            <p className="mt-2 text-slate-400">
              Particuliers & professionnels nous font confiance à Toulouse.
            </p>
          </Reveal>
          <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">
            {STATS.map((s, i) => (
              <Reveal key={s.label} variant="scale" delay={i * 120}>
                <div className="card-lift h-full rounded-2xl border border-slate-800 bg-slate-800/50 p-6 text-center hover:border-sky-500/50 hover:bg-slate-800">
                  <div className="text-4xl font-extrabold text-sky-400">
                    <Counter value={s.value} />
                    {s.suffix && <span className="ml-1 text-lg font-semibold text-sky-300">{s.suffix}</span>}
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{s.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* NOS SERVICES */}
      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal className="mx-auto mb-14 max-w-2xl text-center">
            <span className="text-sm font-semibold uppercase tracking-wider text-sky-600">Nos services</span>
            <h2 className="mt-3 text-3xl font-bold text-slate-900 sm:text-4xl">
              Comment pouvons-nous vous aider ?
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Une solution adaptée à chaque besoin — du nettoyage régulier à la
              blanchisserie professionnelle.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICE_PREVIEWS.map((s, i) => {
              const Icon = iconMap[s.icon];
              return (
                <Reveal key={s.title} variant="up" delay={(i % 3) * 120}>
                  <div className="card-lift group h-full rounded-2xl border border-slate-100 bg-white p-7 shadow-sm hover:border-sky-200 hover:shadow-xl">
                    <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-xl bg-sky-50 text-sky-600 transition-all duration-300 group-hover:scale-110 group-hover:bg-sky-500 group-hover:text-white">
                      <Icon size={26} />
                    </div>
                    <h3 className="mb-2 text-xl font-bold text-slate-900">{s.title}</h3>
                    <p className="mb-5 text-sm leading-relaxed text-slate-600">{s.desc}</p>
                    <Link
                      to="/services"
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-sky-600 transition-all group-hover:gap-2.5"
                    >
                      En savoir plus <ArrowRight size={16} />
                    </Link>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal className="mt-12 text-center">
            <Link to="/services">
              <Button className="btn-shine h-12 rounded-full bg-slate-900 px-8 text-base font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-slate-800">
                Voir tous nos services <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* 3 ETAPES */}
      <section className="bg-sky-50 py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal className="mx-auto mb-14 max-w-2xl text-center">
            <span className="text-sm font-semibold uppercase tracking-wider text-sky-600">3 étapes simples</span>
            <h2 className="mt-3 text-3xl font-bold text-slate-900 sm:text-4xl">
              De la demande au devis, sans friction.
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Un parcours pensé pour vous faire gagner du temps : moins de trois
              minutes suffisent pour obtenir un retour personnalisé.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {STEPS.map((step, i) => (
              <Reveal key={step.num} variant="up" delay={i * 150} className="relative">
                <div className="card-lift rounded-2xl bg-white p-8 shadow-sm hover:shadow-lg">
                  <div className="mb-5 text-5xl font-extrabold text-sky-200">{step.num}</div>
                  <h3 className="mb-3 text-xl font-bold text-slate-900">{step.title}</h3>
                  <p className="text-sm leading-relaxed text-slate-600">{step.desc}</p>
                </div>
                {i < STEPS.length - 1 && (
                  <ArrowRight className="absolute -right-6 top-1/2 hidden -translate-y-1/2 text-sky-300 md:block" size={28} />
                )}
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TEMOIGNAGES */}
      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal className="mx-auto mb-14 max-w-2xl text-center">
            <span className="text-sm font-semibold uppercase tracking-wider text-sky-600">Témoignages</span>
            <h2 className="mt-3 text-3xl font-bold text-slate-900 sm:text-4xl">
              Ce que nos clients disent de nous...
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Les premiers avis seront publiés ici très prochainement. Nous avons
              à cœur de partager des retours authentiques de clients satisfaits.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={i} variant="up" delay={i * 120}>
                <div className="card-lift h-full rounded-2xl border border-slate-100 bg-white p-8 shadow-sm hover:shadow-lg">
                  <Quote className="mb-4 text-sky-300" size={32} />
                  <p className="mb-5 text-sm italic leading-relaxed text-slate-600">“{t.text}”</p>
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-sky-100 font-bold text-sky-600">W</div>
                    <span className="text-sm font-semibold text-slate-800">— {t.author}</span>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-gradient-to-br from-sky-500 to-sky-600 py-16 lg:py-20">
        <div className="pointer-events-none absolute -left-10 -top-10 h-64 w-64 rounded-full bg-white/10 blur-2xl animate-blob" />
        <div className="pointer-events-none absolute -bottom-10 right-0 h-72 w-72 rounded-full bg-cyan-300/20 blur-3xl animate-blob" style={{ animationDelay: "4s" }} />
        <Reveal className="relative mx-auto max-w-4xl px-5 text-center lg:px-8">
          <h2 className="text-3xl font-bold text-white sm:text-4xl">
            Prêt à confier votre nettoyage à des experts ?
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-sky-50">
            Recevez un devis personnalisé sous 24h. Sans engagement, sans surprise
            — uniquement la promesse d'un travail bien fait.
          </p>
          <p className="mt-5 text-sm font-medium text-sky-100">{COMPANY.address}</p>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <Link to="/contact">
              <Button className="btn-shine h-12 w-full rounded-full bg-white px-8 text-base font-semibold text-sky-600 shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:bg-sky-50 sm:w-auto">
                Demander un devis
              </Button>
            </Link>
            <a href={`tel:${COMPANY.phoneRaw}`}>
              <Button
                variant="outline"
                className="h-12 w-full rounded-full border-2 border-white/70 bg-transparent px-8 text-base font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-white/10 sm:w-auto"
              >
                <Phone size={18} className="mr-2" /> {COMPANY.phone}
              </Button>
            </a>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default Home;
